
import React, { useState } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Edit } from 'lucide-react';
import { useClients } from '@/hooks/clients';
import { useAuth } from '@/contexts/AuthContext';
import { useRolePermissions } from '@/hooks/useRolePermissions';
import { useIsMobile } from '@/hooks/use-mobile';
import ClientDetailsHeader from '@/components/clients/ClientDetailsHeader';
import ClientPaymentSection from '@/components/clients/ClientPaymentSection';
import ClientSessionsSection from '@/components/clients/ClientSessionsSection';
import ClientSessionHistorySection from '@/components/clients/ClientSessionHistorySection';
import ClientUpcomingBookingsSection from '@/components/clients/ClientUpcomingBookingsSection';
import ClientForm from '@/components/clients/ClientForm';
import { Link } from 'react-router';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import ClientTermsAcceptanceButton from '@/components/clients/ClientTermsAcceptanceButton';

const ClientDetails: React.FC = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { canManageClients } = useRolePermissions();
  const { data: clients = [], isLoading } = useClients();
  const [showEditModal, setShowEditModal] = useState(false);
  const isMobile = useIsMobile();

  // Smart back navigation function
  const handleBackNavigation = () => {
    try {
      // Try to go back in browser history
      // If user came from dashboard, this will take them back to dashboard
      // If user came from clients list, this will take them back to clients list
      window.history.back();
    } catch (error) {
      // Fallback to clients list if history.back() fails
      navigate('/clients');
    }
  };

  // Handle authentication loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  // Redirect to auth if not logged in
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Find the specific client
  const client = clients.find(c => c.id === id);

  if (isLoading) {
    return (
      <MainLayout>
        <div className="container mx-auto p-4 space-y-6">
          <div className="text-center text-muted-foreground">
            {t('common.loading')}...
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!client) {
    return (
      <MainLayout>
        <div className="container mx-auto p-4 space-y-6">
          <div className="text-center text-muted-foreground">
            {t('clients.clientNotFound')}
          </div>
          <div className="flex justify-center">
            <Button variant="outline" onClick={handleBackNavigation}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('common.back')}
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Mobile accordion content
  const renderMobileAccordion = () => (
    <Accordion type="multiple" className="space-y-4">
      <AccordionItem value="upcoming-bookings">
        <AccordionTrigger>{t('clients.upcomingBookings')}</AccordionTrigger>
        <AccordionContent>
          <ClientUpcomingBookingsSection client={client} />
        </AccordionContent>
      </AccordionItem>
      
      <AccordionItem value="sessions">
        <AccordionTrigger>{t('clients.sessions')}</AccordionTrigger>
        <AccordionContent>
          <ClientSessionsSection client={client} />
        </AccordionContent>
      </AccordionItem>
      
      <AccordionItem value="payment">
        <AccordionTrigger>{t('clients.payment')}</AccordionTrigger>
        <AccordionContent>
          <ClientPaymentSection client={client} />
        </AccordionContent>
      </AccordionItem>
      
      <AccordionItem value="session-history">
        <AccordionTrigger>{t('clients.sessionHistory')}</AccordionTrigger>
        <AccordionContent>
          <ClientSessionHistorySection client={client} />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );

  // Desktop layout
  const renderDesktopLayout = () => (
    <>
      {/* Upcoming Bookings Section - Desktop */}
      <ClientUpcomingBookingsSection client={client} />

      {/* Main Content Grid - Sessions and Payment */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <ClientSessionsSection client={client} />
        <ClientPaymentSection client={client} />
      </div>

      {/* Session History Section */}
      <ClientSessionHistorySection client={client} />
    </>
  );

  return (
    <MainLayout>
      <div className="container mx-auto p-3 md:p-4 space-y-4 md:space-y-6">
        {/* Navigation - Single row with back left, edit and terms buttons right */}
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={handleBackNavigation}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.back')}
          </Button>
          
          <div className="flex items-center gap-2">
            <ClientTermsAcceptanceButton
              clientId={client.id}
              clientName={`${client.firstName} ${client.lastName}`}
              client={{
                id: client.id,
                firstName: client.firstName,
                lastName: client.lastName,
                email: client.email,
                phone: client.phone,
                birthDate: client.birthDate,
                weightKg: client.weightKg,
                heightCm: client.heightCm,
              }}
              size="sm"
            />
            
            {canManageClients && (
              <Button 
                size="sm" 
                onClick={() => setShowEditModal(true)}
              >
                <Edit className="h-4 w-4 mr-2" />
                {t('clients.edit')}
              </Button>
            )}
          </div>
        </div>

        {/* Client Header */}
        <ClientDetailsHeader client={client} />

        {/* Main Content - Responsive Layout */}
        {isMobile ? renderMobileAccordion() : renderDesktopLayout()}

        {/* Edit Client Modal */}
        <ClientForm
          open={showEditModal}
          onOpenChange={setShowEditModal}
          initialData={client}
        />
      </div>
    </MainLayout>
  );
};

export default ClientDetails;
